"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { WNewsletterContent } from "@/lib/api/cms-window-content";
import {
  Field,
  Input,
  Checkbox,
  FormStatus,
  SubmitButton,
} from "@/components/ui/Form";
import { Icon } from "@/lib/icons/Icon";
import { useT } from "@/lib/i18n/context";
import {
  wNewsletterTranslations,
  type WNewsletterTranslations,
} from "./WNewsletter.i18n";
import styles from "./WNewsletter.module.css";

/**
 * WNewsletter - okno `w_newsletter` (po scaleniu z `w_newsletter_popup`).
 *
 * **Charakter:** "teaser" zapisu do newslettera. Zbiera adres e-mail i
 * przekierowuje do katalogu zapisu (`target_catalog.url` - komponent
 * `c_registeration_to_newsletters`), gdzie odbywa się wybór kategorii. Okno
 * NIE zapisuje subskrypcji samodzielnie.
 *
 * **Tryby (`config.display_mode`):**
 *   - `1` inline - formularz renderowany w obszarze (w tym w stopce).
 *   - `2` popup - modal wyświetlany wg `config.show` (1-4, na wejściu/wyjściu),
 *     z cookie `wnl_popup_{window_id}` dla wariantów "jednorazowo".
 */
export function WNewsletter({ content }: WNewsletterProps) {
  const t = useT(wNewsletterTranslations);

  if (!content || typeof content !== "object") {
    return <FormStatus variant="error">{t.noConfig}</FormStatus>;
  }

  if (content.config.display_mode === 2) {
    return <NewsletterPopup content={content} t={t} />;
  }

  return (
    <div className={styles.root}>
      <NewsletterForm content={content} t={t} />
    </div>
  );
}

export type WNewsletterProps = {
  content: WNewsletterContent | null;
};

/* ─── Formularz (wspólny dla inline + popup) ───────────────────────────── */

function NewsletterForm({
  content,
  t,
  onDone,
}: {
  content: WNewsletterContent;
  t: WNewsletterTranslations;
  onDone?: () => void;
}) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; consent?: string }>(
    {},
  );
  const [topError, setTopError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const info = content.lang.info || t.info;
  const placeholder = content.lang.type_email || t.typeEmail;
  const saveLabel = content.lang.save || t.save;
  const requireConsent = content.config.show_right_checkbox;
  const targetUrl = content.target_catalog.url;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTopError(null);

    const errs: { email?: string; consent?: string } = {};
    const em = email.trim();
    /* Okno to TEASER - email jest opcjonalny do przejścia. Walidujemy format
       TYLKO gdy coś wpisano (zła składnia = blok z komunikatem). Katalog docelowy
       (c_registeration_to_newsletters) i tak wymaga emaila + kategorii + zgód i
       robi pełną walidację. Dzięki temu klik "Zapisz się" ZAWSZE prowadzi do
       katalogu (pusty email też), zamiast cicho blokować na pustym polu. */
    if (em && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em))
      errs.email = t.validation.invalidEmail;
    if (requireConsent && !consent) errs.consent = t.validation.requiredConsent;

    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    /* Brak katalogu zapisu = błąd konfiguracji okna - nie ma dokąd przekierować. */
    if (!targetUrl) {
      setTopError(t.noCatalog);
      return;
    }

    setSubmitting(true);
    /* Przejście do strony zapisu z e-mailem w query param (prefill formularza
       c_registeration_to_newsletters, gdzie odbywa się wybór kategorii).
       `ILINK::get()` zwraca URL ABSOLUTNY (https://host/...), a `router.push`
       z next/navigation nie nawiguje do pełnych URL-i (zostawalibyśmy na stronie)
       - dlatego twarda nawigacja `window.location.assign` (jak `<a href>` w
       WLatestNews). Dodatkowo gwarantuje świeży load i odczyt `?email` na docelowej
       stronie. Email doklejamy tylko gdy podany. */
    const url = em
      ? `${targetUrl}${targetUrl.includes("?") ? "&" : "?"}email=${encodeURIComponent(em)}`
      : targetUrl;
    window.location.assign(url);
    onDone?.();
  };

  return (
    <form className={styles.form} onSubmit={onSubmit} noValidate>
      {info && <h3 className={styles.heading}>{info}</h3>}

      {content.config.text && (
        <div
          className={styles.text}
          dangerouslySetInnerHTML={{ __html: content.config.text }}
        />
      )}

      <Field label={placeholder} error={errors.email}>
        <Input
          type="email"
          autoComplete="email"
          value={email}
          disabled={submitting}
          prefix={<Icon name="envelope" />}
          onChange={(e) => setEmail(e.target.value)}
        />
      </Field>

      {requireConsent && (
        <Checkbox
          checked={consent}
          disabled={submitting}
          error={errors.consent}
          onChange={(e) => setConsent(e.target.checked)}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: content.config.processing_personal_data,
            }}
          />
        </Checkbox>
      )}

      {topError && <FormStatus variant="error">{topError}</FormStatus>}

      <div className={styles.actions}>
        <SubmitButton loading={submitting}>{saveLabel}</SubmitButton>
      </div>
    </form>
  );
}

/* ─── Popup (modal + timing + cookie) ──────────────────────────────────── */

function NewsletterPopup({
  content,
  t,
}: {
  content: WNewsletterContent;
  t: WNewsletterTranslations;
}) {
  /* `open` startuje false (SSR renderuje null); staje się true wyłącznie w
     efekcie client-only, więc createPortal(document.body) jest bezpieczny bez
     osobnego mount-guarda. */
  const [open, setOpen] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    const cookieName = `wnl_popup_${content.window_id}`;
    const show = content.config.show;

    /* 1/2 = na wejściu (1 jednorazowo z gate cookie, 2 każdorazowo). */
    if (show === 1 || show === 2) {
      if (show === 1 && getCookie(cookieName)) return;
      if (show === 1) setCookie(cookieName, "1", 30);
      /* Otwarcie popupa na wejściu - celowe, client-only (analog WWindowBanner). */
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOpen(true);
      return;
    }

    /* 3/4 = exit-intent (jednorazowo / każdorazowo). Mysz opuszcza viewport
       górą (clientY <= 0, brak relatedTarget). */
    const onMouseOut = (e: MouseEvent) => {
      if (firedRef.current) return;
      if (e.relatedTarget != null || e.clientY > 0) return;
      if (show === 3 && getCookie(cookieName)) return;
      firedRef.current = true;
      setOpen(true);
      if (show === 3) setCookie(cookieName, "1", 30);
    };
    document.addEventListener("mouseout", onMouseOut);
    return () => document.removeEventListener("mouseout", onMouseOut);
  }, [content.window_id, content.config.show]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className={styles.backdrop} onClick={() => setOpen(false)}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.close}
          aria-label={t.closeLabel}
          onClick={() => setOpen(false)}
        >
          <Icon name="x" />
        </button>
        <NewsletterForm content={content} t={t} onDone={() => setOpen(false)} />
      </div>
    </div>,
    document.body,
  );
}

/* ─── Cookie helpers (client) ──────────────────────────────────────────── */

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
  const m = document.cookie.match(
    new RegExp("(?:^|; )" + escaped + "=([^;]*)"),
  );
  return m ? decodeURIComponent(m[1]) : null;
}

function setCookie(name: string, value: string, days: number): void {
  if (typeof document === "undefined") return;
  const d = new Date();
  d.setTime(d.getTime() + days * 86400000);
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/`;
}
